import React, { useMemo, useState } from 'react';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import { Button, Stack, Typography } from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import appConfig from '../../../config/appConfig';

export const createForgotPasswordEmailSchema = (t) =>
  Yup.object().shape({
    email: Yup.string().email(t.invalid_email_format).required(t.email_required),
  });

const ForgotPasswordEmailForm = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { snackbarMessages, forgotPassword: fp, language } = useTranslatedMessages();
  const authOtpMessages = snackbarMessages.auth_otp ?? {};
  const authPasswordMessages = snackbarMessages.auth_password ?? {};
  const navigate = useNavigate();
  const schema = useMemo(
    () =>
      createForgotPasswordEmailSchema({
        invalid_email_format: fp.validations?.invalid_email_format ?? 'Invalid email format',
        email_required: fp.validations?.email_required ?? 'Email is required',
      }),
    [fp.validations],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/forgot-password', data)
      .then((res) => {
        navigate('/forgot-password', { replace: true, state: { email: data.email, stage: 'verification' } });
        dispatch(showSnackbar({ message: authOtpMessages.otp_sent_success, severity: 'success' }));
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.errors?.message ?? authPasswordMessages.forgot_password_generic_error,
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>
      <Stack spacing={2}>
        <CustomTextInput name="email" label={fp.email_form?.email_label ?? 'Email'} control={control} errors={errors} />
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)}>
          {loading ? (fp.email_form?.sending_otp ?? 'Sending OTP...') : (fp.email_form?.send_otp ?? 'Send OTP')}
        </Button>
        <Typography variant="body2">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            {fp.email_form?.back_to_login ?? 'Back to Login'}
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default ForgotPasswordEmailForm;
